import React, { useContext } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import wongBakerFaces from "./wongBakerFaces.png";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerGrid,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface chestPainFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const ChestPainForm: React.FC<chestPainFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const userRole = sessionStorage.getItem("userRole");

  const { isLoading: isLoadingChestPainMutation, mutate: submitChestPainForm } =
    useMutation<string>(
      "add-bill",
      async () => {
        return await createApiClient().addChestPainFormData({
          patient_internal_id:
            userRole === UserRoles.PATIENT && userContext.userData
              ? userContext.userData?.internal_id
              : patientContext?.patientData?.id || "",
          chestpain: localStorage.getItem("hasChestPain") || "Yes",
          recentpain: form.getFieldValue("Most recent episode") || "",
          restpain: form.getFieldValue("Chest pain at rest") || "",
          level:
            localStorage.getItem("levelOfChestPain") ||
            form.getFieldValue("Level of chest pain") ||
            "",
          type: form.getFieldValue("Type of chest pain") || [],
          length: form.getFieldValue("Duration of chest pain") || "",
          worse: form.getFieldValue("What worsens chest pain") || [],
          better: form.getFieldValue("What relieves chest pain") || [],
          frequency: form.getFieldValue("Frequency of chest pain") || "",
          submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
        });
      },
      {
        onSuccess: () => {
          message.success("Form Submitted Successfully");
          goToNextFormOrReturn();
        },
        onError: () => {
          message.error({
            content: (
              <span>
                There was a problem submitting this form, resubmit or{" "}
                <a onClick={() => goToNextFormOrReturn()}>
                  click here to continue to the next survey
                </a>
              </span>
            ),
            style: {
              bottom: "10px",
              position: "fixed",
              left: "30%"
            },
            duration: 5
          });
        }
      }
    );

  const onFinish = () => {
    submitChestPainForm();
  };

  return (
    <SurveyPage platform={platform}>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="chestPain"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          <SurveyNoticeCard />

          <SurveyQuestionCard
            title="When was the most recent episode of chest pain?"
            required
          >
            <FormItemStyle
              name="Most recent episode"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group value={""} style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="1 - 2 days ago">1 - 2 days ago</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="3 - 7 days ago">3 - 7 days ago</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="8 - 14 days ago">8 - 14 days ago</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="15 - 30 days ago">15 - 30 days ago</Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="Do you have chest pain at rest?" required>
            <FormItemStyle
              name="Chest pain at rest"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group value={""} style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="Yes">Yes</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="No">No</Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="How severe is your chest pain?" required>
            <FormItemStyle
              name="Level of chest pain"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group
                style={{ width: "100%" }}
                onChange={e =>
                  localStorage.setItem("levelOfChestPain", e.target.value)
                }
              >
                <AnswerGrid columns={5}>
                  {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(
                    val => (
                      <AnswerOption key={val}>
                        <Radio value={val}>{val}</Radio>
                      </AnswerOption>
                    )
                  )}
                </AnswerGrid>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard style={{ textAlign: "center" }}>
            <img
              src={wongBakerFaces}
              alt="Wong Baker Faces Scale"
              style={{
                maxHeight: platform === Platform.MOBILE ? "120px" : "225px",
                maxWidth: "100%",
                objectFit: "contain"
              }}
            />
          </SurveyQuestionCard>

          <SurveyQuestionCard title="Where is your pain?" required>
            <FormItemStyle
              name="Type of chest pain"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Checkbox value="Heaviness">Heaviness</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Dull/Aching">Dull/Aching</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Sharp/Stabbing">Sharp/Stabbing</Checkbox>
                  </AnswerOption>
                </AnswerList>
              </Checkbox.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard
            title="How long did the chest pain last? (List longest episode)"
            required
          >
            <FormItemStyle
              name="Duration of chest pain"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group value={""} style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="Less than 5 mins">Less than 5 mins</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="5 to 15 mins">5 to 15 mins</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="15 to 30 mins">15 to 30 mins</Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="More than 30 mins">More than 30 mins</Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="What makes the chest pain worse?">
            <FormItemStyle
              name="What worsens chest pain"
              label=""
              initialValue={[]}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Checkbox value="Exertion">Exertion</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Deep breathing/coughing">
                      Deep breathing/coughing
                    </Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Movement">Movement</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Lying flat">Lying flat</Checkbox>
                  </AnswerOption>
                </AnswerList>
              </Checkbox.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard title="What makes the chest pain better?">
            <FormItemStyle
              name="What relieves chest pain"
              label=""
              initialValue={[]}
            >
              <Checkbox.Group style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Checkbox value="Nothing/unrelieved by rest or medicine">
                      Nothing/unrelieved by rest or medicine
                    </Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Resting">Resting</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Shallow breathing">
                      Shallow breathing
                    </Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Sitting forward">Sitting forward</Checkbox>
                  </AnswerOption>
                  <AnswerOption>
                    <Checkbox value="Medication">Medication</Checkbox>
                  </AnswerOption>
                </AnswerList>
              </Checkbox.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyQuestionCard
            title="How often has the chest pain occurred in the last one month?"
            required
          >
            <FormItemStyle
              name="Frequency of chest pain"
              label=""
              rules={[{ required: true }]}
              initialValue={""}
            >
              <Radio.Group value={""} style={{ width: "100%" }}>
                <AnswerList>
                  <AnswerOption>
                    <Radio value="1 - 2 times per month">
                      1 - 2 times per month
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="3 - 5 times per month">
                      3 - 5 times per month
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="6 - 10 times per month">
                      6 - 10 times per month
                    </Radio>
                  </AnswerOption>
                  <AnswerOption>
                    <Radio value="More than 10 times per month">
                      More than 10 times per month
                    </Radio>
                  </AnswerOption>
                </AnswerList>
              </Radio.Group>
            </FormItemStyle>
          </SurveyQuestionCard>

          <SurveyFooter>
            <SurveyProgressBar currentPage={1} totalPages={1} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={() => form.resetFields()} />
              </Form.Item>

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent="Submit"
                  htmlType="submit"
                  loading={isLoadingChestPainMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
